import React, { useEffect, useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Mail } from "lucide-react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "sonner";

import { Button } from "@/src/components/ui/button";

import LoginField from "./LoginField";
import { createResetPasswordSchema } from "./loginSchemas";
import LoginTitle from "./LoginTitle";
import AdminUserAPI from "../../api/AdminUserAPI";
import i18n from "../../i18n";
import history from "../common/navigation/AppHistory";

import type { JSX } from "react";

import type { ResetPasswordFormValues } from "./loginSchemas";
import type { AppState } from "../../reducers";

interface ResetPasswordProps {
  redirectTo: string;
  resetRoute: string;
}

function ResetPassword({
  redirectTo,
  resetRoute,
}: ResetPasswordProps): JSX.Element {
  const { t } = useTranslation("login");

  const isLoggedIn = useSelector(
    (state: AppState) => state.loginReducer.isLoggedIn,
  );

  const schema = useMemo(() => createResetPasswordSchema(t), [t]);
  const methods = useForm<ResetPasswordFormValues>({
    defaultValues: { email: "" },
    mode: "onBlur",
    resolver: zodResolver(schema),
  });

  const { isSubmitting } = methods.formState;

  useEffect(() => {
    if (isLoggedIn) {
      history.push("/");
    }
  }, [isLoggedIn]);

  const onValid = async ({ email }: ResetPasswordFormValues): Promise<void> => {
    try {
      await AdminUserAPI.resetPassword({
        email,
        resetRoute,
        language:
          (i18n.language || i18n.options.fallbackLng?.toString()) ?? "en",
      });

      toast.success(t("successResetPassword"));
      history.push(redirectTo);
    } catch (error) {
      toast.error(t("genericError", { ns: "error" }));
      console.error("Error during password reset request:", error);
    }
  };

  return (
    <React.Fragment>
      <LoginTitle>{t("resetPasswordTitle")}</LoginTitle>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <FormProvider {...methods}>
        <form
          noValidate
          className="flex w-full flex-col gap-5"
          onSubmit={methods.handleSubmit(onValid)}
        >
          <LoginField
            autoComplete="email"
            icon={Mail}
            label={t("emailLabel")}
            name="email"
            placeholder={t("emailContent")}
            type="email"
          />

          <Button
            className="mt-3 w-full"
            disabled={isSubmitting}
            size="lg"
            type="submit"
          >
            {t("resetButtonLabel")}
          </Button>
        </form>
      </FormProvider>
    </React.Fragment>
  );
}

export default ResetPassword;
